import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, Circle, X, Sparkles, ArrowRight } from 'lucide-react';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { cn } from '@/lib/utils';
import { useGettingStartedChecklist } from '@/hooks/useGettingStartedChecklist';

export function GettingStartedChecklist() {
  const { orgId } = useAdminOrg();
  const [dismissed, setDismissed] = useState(false);

  const dismissKey = `gs-checklist-dismissed-${orgId}`;

  // Non-data effect: read dismiss flag from localStorage whenever orgId changes.
  useEffect(() => {
    if (!orgId) return;
    setDismissed(localStorage.getItem(dismissKey) === 'true');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const { data: items = [], isLoading: loading } = useGettingStartedChecklist(orgId);

  const handleDismiss = () => {
    localStorage.setItem(dismissKey, 'true');
    setDismissed(true);
  };

  if (loading || dismissed) return null;

  const completed = items.filter((i) => i.done).length;
  const total = items.length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  // Auto-hide once everything is done
  if (completed === total && total > 0) {
    return (
      <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
        <CardContent className="flex items-center justify-between gap-4 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold">You're all set up — nice work!</p>
              <p className="text-sm text-muted-foreground">
                Your business is ready to take bookings.
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={handleDismiss} aria-label="Dismiss">
            <X className="h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="h-5 w-5 text-primary" />
              Getting started
            </CardTitle>
            <CardDescription className="mt-1">
              {completed} of {total} complete — finish setup to launch your business.
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDismiss}
            aria-label="Dismiss checklist"
            className="-mr-2 -mt-1"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <Progress value={percent} className="mt-3 h-2" />
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((item) => (
          <Link
            key={item.id}
            to={item.href}
            className={cn(
              'group flex items-center justify-between gap-3 rounded-lg border p-3 transition-colors',
              item.done
                ? 'border-transparent bg-muted/40'
                : 'border-border bg-background hover:border-primary/40 hover:bg-muted/40'
            )}
          >
            <div className="flex items-center gap-3 min-w-0">
              {item.done ? (
                <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />
              ) : (
                <Circle className="h-5 w-5 shrink-0 text-muted-foreground" />
              )}
              <div className="min-w-0">
                <p
                  className={cn(
                    'text-sm font-medium truncate',
                    item.done && 'text-muted-foreground line-through'
                  )}
                >
                  {item.title}
                </p>
                {!item.done && (
                  <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                )}
              </div>
            </div>
            {!item.done && (
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
            )}
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}
